import { useState, useCallback, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Plus,
  Volume2,
  Loader2,
  RefreshCw,
  FileText,
  X,
  List,
  LayoutGrid,
  MoveRight,
  Square,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import { Button, Input } from "@learn-me/ui";
import { MiniRichTextEditor } from "@/components/editor/MiniRichTextEditor";
import { adminApi, type TTSVoice, type ChapterSection } from "../api/adminApi";
import { SortableSectionCard } from "./SortableSectionCard";
import { BulkSectionEditor } from "./BulkSectionEditor";

// Simple Modal component
function Modal({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
        onKeyDown={(e) => e.key === "Escape" && onClose()}
      />
      <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {children}
      </div>
    </div>
  );
}

interface SectionEditorProps {
  chapterId: string;
  courseId?: string;
}

export function SectionEditor({ chapterId, courseId }: SectionEditorProps) {
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<"list" | "bulk">("list");
  const [selectedVoice, setSelectedVoice] =
    useState<string>("es-MX-DaliaNeural");
  const [isAddingSection, setIsAddingSection] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState("");
  const [newSectionContent, setNewSectionContent] = useState("");
  const [generatingAudioFor, setGeneratingAudioFor] = useState<string | null>(
    null,
  );
  const [deletingAudioFor, setDeletingAudioFor] = useState<string | null>(null);
  const [savingSectionId, setSavingSectionId] = useState<string | null>(null);
  const [movingSection, setMovingSection] = useState<ChapterSection | null>(
    null,
  );
  const [targetChapterId, setTargetChapterId] = useState<string>("");

  // Batch audio generation state
  const [batchProgress, setBatchProgress] = useState<{
    isRunning: boolean;
    current: number;
    total: number;
    currentSection: string;
    completed: string[];
    failed: string[];
  } | null>(null);
  const batchCancelledRef = useRef(false);

  // Fetch sections
  const { data: sections = [], isLoading: isLoadingSections } = useQuery({
    queryKey: ["chapter-sections", chapterId],
    queryFn: () => adminApi.getChapterSections(chapterId),
  });

  // Fetch voices
  const { data: voices = [] } = useQuery<TTSVoice[]>({
    queryKey: ["tts-voices"],
    queryFn: () => adminApi.getTTSVoices(),
  });

  // Fetch course chapters for move functionality
  const { data: courseData } = useQuery({
    queryKey: ["admin-course", courseId],
    queryFn: () => adminApi.getCourse(courseId!),
    enabled: !!courseId && !!movingSection,
  });

  const otherChapters =
    courseData?.chapters?.filter((ch) => ch.id !== chapterId) ?? [];

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Parse content to sections mutation
  const parseSectionsMutation = useMutation({
    mutationFn: () => adminApi.parseContentToSections(chapterId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["chapter-sections", chapterId],
      });
      toast.success("Contenido parseado en secciones");
    },
    onError: () => {
      toast.error("Error al parsear contenido");
    },
  });

  // Create section mutation
  const createSectionMutation = useMutation({
    mutationFn: (data: { title: string; content: string }) =>
      adminApi.createSection(chapterId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["chapter-sections", chapterId],
      });
      // Invalidar capítulo para sincronizar contenido
      queryClient.invalidateQueries({
        queryKey: ["chapter-edit", chapterId],
      });
      queryClient.invalidateQueries({
        queryKey: ["chapter", chapterId],
      });
      setIsAddingSection(false);
      setNewSectionTitle("");
      setNewSectionContent("");
      toast.success("Seccion creada");
    },
    onError: () => {
      toast.error("Error al crear seccion");
    },
  });

  // Update section mutation
  const updateSectionMutation = useMutation({
    mutationFn: ({
      sectionId,
      data,
    }: {
      sectionId: string;
      data: { title?: string; content?: string };
    }) => adminApi.updateSection(sectionId, data),
    onSuccess: () => {
      // Invalidar secciones
      queryClient.invalidateQueries({
        queryKey: ["chapter-sections", chapterId],
      });
      // Invalidar capítulo para que el player refleje los cambios
      queryClient.invalidateQueries({
        queryKey: ["chapter-edit", chapterId],
      });
      queryClient.invalidateQueries({
        queryKey: ["chapter", chapterId],
      });
      setSavingSectionId(null);
      toast.success("Seccion actualizada");
    },
    onError: () => {
      setSavingSectionId(null);
      toast.error("Error al actualizar seccion");
    },
  });

  // Delete section mutation
  const deleteSectionMutation = useMutation({
    mutationFn: (sectionId: string) => adminApi.deleteSection(sectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["chapter-sections", chapterId],
      });
      // Invalidar capítulo para sincronizar contenido
      queryClient.invalidateQueries({
        queryKey: ["chapter-edit", chapterId],
      });
      queryClient.invalidateQueries({
        queryKey: ["chapter", chapterId],
      });
      toast.success("Seccion eliminada");
    },
    onError: () => {
      toast.error("Error al eliminar seccion");
    },
  });

  // Reorder sections mutation
  const reorderMutation = useMutation({
    mutationFn: (sectionIds: string[]) =>
      adminApi.reorderSections(chapterId, sectionIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["chapter-sections", chapterId],
      });
      // Invalidar capítulo para sincronizar contenido
      queryClient.invalidateQueries({
        queryKey: ["chapter-edit", chapterId],
      });
      queryClient.invalidateQueries({
        queryKey: ["chapter", chapterId],
      });
    },
    onError: () => {
      toast.error("Error al reordenar secciones");
    },
  });

  // Generate section audio mutation
  const generateAudioMutation = useMutation({
    mutationFn: (sectionId: string) =>
      adminApi.generateSectionAudio(sectionId, { voice: selectedVoice }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["chapter-sections", chapterId],
      });
      setGeneratingAudioFor(null);
      toast.success("Audio generado");
    },
    onError: () => {
      setGeneratingAudioFor(null);
      toast.error("Error al generar audio");
    },
  });

  // Delete section audio mutation
  const deleteAudioMutation = useMutation({
    mutationFn: (sectionId: string) => adminApi.deleteSectionAudio(sectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["chapter-sections", chapterId],
      });
      setDeletingAudioFor(null);
      toast.success("Audio eliminado");
    },
    onError: () => {
      setDeletingAudioFor(null);
      toast.error("Error al eliminar audio");
    },
  });

  // Generate all audios - sequential with progress tracking
  const handleGenerateAllAudios = useCallback(async () => {
    const sectionsWithoutAudio = sections.filter((s) => !s.audioUrl);
    if (sectionsWithoutAudio.length === 0) {
      toast.success("Todas las secciones ya tienen audio");
      return;
    }

    batchCancelledRef.current = false;
    setBatchProgress({
      isRunning: true,
      current: 0,
      total: sectionsWithoutAudio.length,
      currentSection: sectionsWithoutAudio[0]?.title || "",
      completed: [],
      failed: [],
    });

    const completed: string[] = [];
    const failed: string[] = [];

    for (let i = 0; i < sectionsWithoutAudio.length; i++) {
      if (batchCancelledRef.current) {
        toast.success(
          `Generación cancelada. ${completed.length} audios generados.`,
        );
        break;
      }

      const section = sectionsWithoutAudio[i];
      if (!section) continue;

      setBatchProgress((prev) =>
        prev
          ? {
              ...prev,
              current: i + 1,
              currentSection: section.title,
            }
          : null,
      );

      try {
        await adminApi.generateSectionAudio(section.id, {
          voice: selectedVoice,
        });
        completed.push(section.id);
        setBatchProgress((prev) =>
          prev ? { ...prev, completed: [...prev.completed, section.id] } : null,
        );
      } catch {
        failed.push(section.id);
        setBatchProgress((prev) =>
          prev ? { ...prev, failed: [...prev.failed, section.id] } : null,
        );
      }
    }

    // Finish
    queryClient.invalidateQueries({
      queryKey: ["chapter-sections", chapterId],
    });

    if (!batchCancelledRef.current) {
      if (failed.length > 0) {
        toast.error(
          `${completed.length} audios generados, ${failed.length} fallaron`,
        );
      } else {
        toast.success(`${completed.length} audios generados exitosamente`);
      }
    }

    setBatchProgress(null);
  }, [sections, selectedVoice, chapterId, queryClient]);

  const handleCancelBatch = useCallback(() => {
    batchCancelledRef.current = true;
  }, []);

  // Move section mutation
  const moveSectionMutation = useMutation({
    mutationFn: ({
      sectionId,
      targetChapterId: targetId,
    }: {
      sectionId: string;
      targetChapterId: string;
    }) => adminApi.moveSection(sectionId, targetId),
    onSuccess: (_, { targetChapterId: targetId }) => {
      // Invalidate both source and target chapter sections
      queryClient.invalidateQueries({
        queryKey: ["chapter-sections", chapterId],
      });
      queryClient.invalidateQueries({
        queryKey: ["chapter-sections", targetId],
      });
      // Invalidar capítulos para sincronizar contenido
      queryClient.invalidateQueries({
        queryKey: ["chapter-edit", chapterId],
      });
      queryClient.invalidateQueries({
        queryKey: ["chapter", chapterId],
      });
      queryClient.invalidateQueries({
        queryKey: ["chapter-edit", targetId],
      });
      queryClient.invalidateQueries({
        queryKey: ["chapter", targetId],
      });
      setMovingSection(null);
      setTargetChapterId("");
      toast.success("Sección movida exitosamente");
    },
    onError: () => {
      toast.error("Error al mover la sección");
    },
  });

  // Handle move section
  const handleMoveSection = useCallback(() => {
    if (movingSection && targetChapterId) {
      moveSectionMutation.mutate({
        sectionId: movingSection.id,
        targetChapterId,
      });
    }
  }, [movingSection, targetChapterId, moveSectionMutation]);

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sections.findIndex((s) => s.id === active.id);
      const newIndex = sections.findIndex((s) => s.id === over.id);

      // Create new order
      const newSections = [...sections];
      const [removed] = newSections.splice(oldIndex, 1);
      if (removed) {
        newSections.splice(newIndex, 0, removed);
      }

      // Update positions
      reorderMutation.mutate(newSections.map((s) => s.id));
    }
  };

  const handleGenerateAudio = (sectionId: string) => {
    setGeneratingAudioFor(sectionId);
    generateAudioMutation.mutate(sectionId);
  };

  const handleDeleteAudio = (sectionId: string) => {
    setDeletingAudioFor(sectionId);
    deleteAudioMutation.mutate(sectionId);
  };

  const handleCreateSection = () => {
    if (!newSectionTitle.trim()) {
      toast.error("El titulo es requerido");
      return;
    }
    createSectionMutation.mutate({
      title: newSectionTitle.trim(),
      content: newSectionContent || "<p></p>",
    });
  };

  // Inline save handler for SortableSectionCard
  const handleSaveSection = useCallback(
    async (sectionId: string, data: { title: string; content: string }) => {
      setSavingSectionId(sectionId);
      await updateSectionMutation.mutateAsync({
        sectionId,
        data,
      });
    },
    [updateSectionMutation],
  );

  const sectionsWithAudio = sections.filter((s) => s.audioUrl).length;
  const totalSections = sections.length;

  if (isLoadingSections) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900">Secciones</h3>
          <span className="text-sm text-gray-500">
            ({totalSections} secciones, {sectionsWithAudio} con audio)
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* View mode toggle */}
          {sections.length > 0 && (
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={`px-3 py-1.5 text-sm flex items-center gap-1 transition-colors ${
                  viewMode === "list"
                    ? "bg-primary-600 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
                title="Vista de lista"
              >
                <List className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("bulk")}
                className={`px-3 py-1.5 text-sm flex items-center gap-1 transition-colors ${
                  viewMode === "bulk"
                    ? "bg-primary-600 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
                title="Edicion masiva"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Voice selector */}
          <select
            value={selectedVoice}
            onChange={(e) => setSelectedVoice(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1.5"
          >
            {voices.map((voice) => (
              <option key={voice.id} value={voice.id}>
                {voice.name}
              </option>
            ))}
          </select>

          {/* Parse content button */}
          {sections.length === 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => parseSectionsMutation.mutate()}
              disabled={parseSectionsMutation.isPending}
            >
              {parseSectionsMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <FileText className="h-4 w-4 mr-1" />
              )}
              Parsear Contenido
            </Button>
          )}

          {/* Generate all audios */}
          {sections.length > 0 && viewMode === "list" && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleGenerateAllAudios}
              disabled={!!batchProgress}
            >
              {batchProgress ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Volume2 className="h-4 w-4 mr-1" />
              )}
              Generar Todos los Audios
            </Button>
          )}

          {/* Add section */}
          {viewMode === "list" && (
            <Button
              type="button"
              size="sm"
              onClick={() => setIsAddingSection(true)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Agregar Seccion
            </Button>
          )}
        </div>
      </div>

      {/* Batch progress indicator */}
      {batchProgress && (
        <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
              <span className="font-medium text-indigo-800">
                Generando audios...
              </span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCancelBatch}
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              <Square className="h-4 w-4 mr-1" />
              Cancelar
            </Button>
          </div>

          {/* Progress bar */}
          <div className="space-y-1">
            <div className="flex justify-between text-sm text-indigo-700">
              <span>Procesando: {batchProgress.currentSection}</span>
              <span>
                {batchProgress.current} de {batchProgress.total}
              </span>
            </div>
            <div className="w-full h-3 bg-indigo-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-indigo-600 transition-all duration-300"
                style={{
                  width: `${(batchProgress.current / batchProgress.total) * 100}%`,
                }}
              />
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1 text-green-600">
              <CheckCircle className="h-4 w-4" />
              {batchProgress.completed.length} completados
            </span>
            {batchProgress.failed.length > 0 && (
              <span className="flex items-center gap-1 text-red-600">
                <AlertCircle className="h-4 w-4" />
                {batchProgress.failed.length} fallidos
              </span>
            )}
          </div>
        </div>
      )}

      {/* Sections list */}
      {sections.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
          <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 mb-4">
            No hay secciones definidas para este capitulo
          </p>
          <div className="flex items-center justify-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => parseSectionsMutation.mutate()}
              disabled={parseSectionsMutation.isPending}
            >
              {parseSectionsMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1" />
              )}
              Parsear desde Contenido
            </Button>
            <Button type="button" onClick={() => setIsAddingSection(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Crear Manualmente
            </Button>
          </div>
        </div>
      ) : viewMode === "bulk" ? (
        <BulkSectionEditor
          chapterId={chapterId}
          sections={sections}
          onSave={() => {
            queryClient.invalidateQueries({
              queryKey: ["chapter-sections", chapterId],
            });
          }}
          onCancel={() => setViewMode("list")}
        />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sections.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {sections.map((section) => (
                <SortableSectionCard
                  key={section.id}
                  section={section}
                  onSave={handleSaveSection}
                  onDelete={(id) => deleteSectionMutation.mutate(id)}
                  onMove={(s) => setMovingSection(s)}
                  onGenerateAudio={handleGenerateAudio}
                  onDeleteAudio={handleDeleteAudio}
                  isGeneratingAudio={generatingAudioFor === section.id}
                  isDeletingAudio={deletingAudioFor === section.id}
                  isSaving={savingSectionId === section.id}
                  canMove={!!courseId && otherChapters.length > 0}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Add Section Modal */}
      <Modal open={isAddingSection} onClose={() => setIsAddingSection(false)}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Nueva Seccion</h3>
            <button
              type="button"
              onClick={() => setIsAddingSection(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titulo
              </label>
              <Input
                value={newSectionTitle}
                onChange={(e) => setNewSectionTitle(e.target.value)}
                placeholder="Titulo de la seccion"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contenido
              </label>
              <MiniRichTextEditor
                content={newSectionContent}
                onChange={setNewSectionContent}
                placeholder="Escribe el contenido de la seccion..."
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsAddingSection(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleCreateSection}
              disabled={createSectionMutation.isPending}
            >
              {createSectionMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : null}
              Crear Seccion
            </Button>
          </div>
        </div>
      </Modal>

      {/* Move Section Modal */}
      <Modal
        open={!!movingSection}
        onClose={() => {
          setMovingSection(null);
          setTargetChapterId("");
        }}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Mover Sección</h3>
            <button
              type="button"
              onClick={() => {
                setMovingSection(null);
                setTargetChapterId("");
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {movingSection && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Sección a mover:</p>
                <p className="font-medium">{movingSection.title}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mover a capítulo:
                </label>
                {otherChapters.length > 0 ? (
                  <select
                    value={targetChapterId}
                    onChange={(e) => setTargetChapterId(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Seleccionar capítulo...</option>
                    {otherChapters.map((ch) => (
                      <option key={ch.id} value={ch.id}>
                        {ch.position}. {ch.title}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    No hay otros capítulos disponibles
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setMovingSection(null);
                setTargetChapterId("");
              }}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleMoveSection}
              disabled={!targetChapterId || moveSectionMutation.isPending}
            >
              {moveSectionMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <MoveRight className="h-4 w-4 mr-1" />
              )}
              Mover Sección
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
